import { refreshToken } from "store/slices/authSlice"
import { redirectToLoginAfterSessionEnd } from "utils/sessionCleanup"
import logger from "utils/logger"
import RequestDeduplicator from "utils/requestDeduplicator"
import { shouldRedirectToForbidden } from "utils/apiForbiddenRedirect"
import { redirectOnApiForbidden } from "utils/redirectOnForbidden"

const deduplicator = new RequestDeduplicator()

/**
 * Redux middleware that intercepts rejected async-thunk actions and handles
 * common HTTP error scenarios:
 *
 *  - 401 Unauthorized  → attempt token refresh; redirect to /login on failure
 *  - 403 Forbidden     → redirect to first accessible route
 *  - 500 Server Error  → log and surface the error
 *
 * It also prevents concurrent identical requests using RequestDeduplicator.
 */
const apiErrorMiddleware = store => next => action => {
  // ── Request deduplication ────────────────────────────────────────────────
  // Only deduplicate pending thunk actions (those dispatched by createAsyncThunk)
  if (
    action?.meta?.requestId &&
    action?.meta?.requestStatus === "pending" &&
    action?.meta?.arg?.deduplicate !== false
  ) {
    const key = `${action.type}::${JSON.stringify(action.meta.arg || "")}`
    if (deduplicator.isPending(key)) {
      // Silently drop – another identical request is already running
      return
    }
    // Register this request as pending so duplicates are dropped
    const executorPromise = Promise.resolve(next(action))
    deduplicator.request(key, () => executorPromise)
    return executorPromise
  }

  // ── Error handling for rejected thunks ───────────────────────────────────
  if (action?.meta?.requestStatus === "rejected") {
    const error = action.payload || action.error

    // Extract HTTP status from the error if attached
    const status =
      error?.status ||
      error?.response?.status ||
      action?.meta?.baseQueryMeta?.response?.status

    if (status === 401) {
      logger.warn("[apiErrorMiddleware] 401 – attempting token refresh")
      store
        .dispatch(refreshToken())
        .unwrap()
        .catch(() => {
          logger.error("[apiErrorMiddleware] Token refresh failed – redirecting to /login")
          redirectToLoginAfterSessionEnd("/login")
        })
    }

    if (status === 403 && shouldRedirectToForbidden(error, error?.config)) {
      logger.warn(
        "[apiErrorMiddleware] 403 – permission denied, redirecting to first accessible route"
      )
      redirectOnApiForbidden()
    }

    if (status === 500) {
      logger.error("[apiErrorMiddleware] 500 – internal server error", error)
    }
  }

  return next(action)
}

export default apiErrorMiddleware
